import './style.css';
import { FunctionalComponent, h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { dateRange, todayStr } from './mock/data';
import { fetchSchedule } from './dataSource';
import { EmptyState } from './state';
import { withGhId } from './apiConfig';
import TabBar from './components/TabBar';
import type { Session } from './mock/types';
import { usePageView } from './usePageView';

const TODAY = todayStr();
const DATES = dateRange(7);

const STATUS: Record<string, { cls: string; label: string }> = {
  living: { cls: 'living', label: '直播中' },
  pending: { cls: 'pending', label: '待开播' },
  ended: { cls: 'ended', label: '已结束' },
};

const ANCHOR_INITIAL: Record<number, string> = { 1: '李', 2: '女', 3: '衣' };

/** 直播日历（C01–C04）：按日期聚合全部主播排期；场次卡含 ≤3 预览图 + 仅「查看商品清单」入口 */
const TrailerCalendar: FunctionalComponent = () => {
  usePageView('calendar');
  const [date, setDate] = useState(TODAY);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = () => {
    setLoading(true);
    setFailed(false);
    fetchSchedule(TODAY, DATES[DATES.length - 1])
      .then((s) => setSessions(s))
      .catch(() => setFailed(true)) // C04：失败保留日期可重试
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const daySessions = sessions.filter((s) => s.date === date);

  return (
    <div class="vp-page">
      <div class="vp-topbar vp-page-titlebar">
        <h1>直播日历</h1>
        <span class="vp-page-subtitle">全部主播排期</span>
      </div>
      <div class="vp-host-search-wrap">
        <button class="vp-host-search" onClick={() => (location.href = withGhId('/live/search'))}>
          <i class="search-ico" />搜索全站商品 / 品牌 / 主播
        </button>
      </div>

      <div class="vp-datestrip">
        <button class="strip-arrow" aria-label="前一天" onClick={() => setDate(DATES[Math.max(0, DATES.indexOf(date) - 1)])}>‹</button>
        <div class="vp-dates">
          {DATES.map((d) => {
            const hasMark = sessions.some((s) => s.date === d);
            return (
              <button key={d} class={`vp-date${d === date ? ' selected' : ''}`} onClick={() => setDate(d)}>
                {d === date ? <i class="halo" /> : null}
                <i class={`vp-date-dot${hasMark ? ' has-schedule' : ''}`} />
                <b class="vp-date-num">{parseInt(d.slice(8), 10)}</b>
                <span class="vp-date-week">{d === TODAY ? '今天' : ['周日','周一','周二','周三','周四','周五','周六'][new Date(d + 'T00:00:00').getDay()]}</span>
              </button>
            );
          })}
        </div>
        <button class="strip-arrow" aria-label="后一天" onClick={() => setDate(DATES[Math.min(DATES.length - 1, DATES.indexOf(date) + 1)])}>›</button>
      </div>

      <div class="vp-calendar-heading">
        <b>
          {date === TODAY ? '今天' : `${parseInt(date.slice(5, 7), 10)}月${parseInt(date.slice(8), 10)}日`} 的直播
        </b>
        <span>{loading ? '正在加载排期…' : `${daySessions.length} 场 · 全部主播排期`}</span>
      </div>

      {loading ? (
        <div class="vp-list-end">正在加载预告…</div>
      ) : failed ? (
        <EmptyState
          spec={{ title: '预告暂时加载失败', desc: '已保留当前日期', actions: [{ label: '重新加载', kind: 'reload' }] }}
          onAction={load}
        />
      ) : daySessions.length === 0 ? (
        <EmptyState
          spec={{ title: '这一天还没有预告', desc: '新场次发布后会显示在这里\n先看看其他日期的商品吧', actions: [{ label: '查看今天', kind: 'today' }] }}
          onAction={() => setDate(TODAY)}
        />
      ) : (
        daySessions.map((s) => {
          const anchor = s.anchorId ? { name: ['李佳琦', '所有女生', '所有女生的衣橱'][s.anchorId - 1] ?? '直播间', initial: ANCHOR_INITIAL[s.anchorId] ?? '直' } : { name: '直播间', initial: '直' };
          const chip = STATUS[s.status];
          return (
            <div
              key={s.id}
              class="vp-calendar-card"
              onClick={() => (location.href = withGhId(`/live?anchor=${s.anchorId}&date=${s.date}&session=${s.id}`))}
            >
              <div class="vp-calendar-card-head">
                <i class="vp-avatar vp-calendar-avatar">{anchor.initial}</i>
                <div class="vp-calendar-host-meta">
                  <b class="vp-calendar-host">{anchor.name}</b>
                  <span class="vp-calendar-time">{s.startTime} 开播</span>
                </div>
                <span class={`vp-chip ${chip.cls}`}>{chip.label}</span>
              </div>
              <div class="vp-calendar-card-body">
                <span class="vp-calendar-theme">{s.theme}</span>
                {s.previews && s.previews.length > 0 ? (
                  <div class="vp-cal-preview">
                    {s.previews.map((p, i) => (
                      <span key={i} class="vp-cal-thumb">
                        <img src={p.image} alt="" />
                        <i>{p.price ? `¥${p.price}` : '待公布'}</i>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span class="vp-cal-none">商品清单待公布</span>
                )}
                <div class="vp-calendar-card-footer">
                <button
                  class="vp-card-btn compact solid"
                  onClick={(e) => { e.stopPropagation(); location.href = withGhId(`/live?anchor=${s.anchorId}&date=${s.date}&session=${s.id}`); }}
                >
                  查看商品清单 ›
                </button>
                </div>
              </div>
            </div>
          );
        })
      )}

      <div class="vp-tab-content-pad" />
      <TabBar active="calendar" />
    </div>
  );
};

export default TrailerCalendar;
